// @ts-check

import axios, { AxiosError } from 'axios';
import { DRIVO_URL } from "../drivoApollo";
import { getAuthHeaders } from '../authHeaders';
import RequestError from '../RequestError';

/** @typedef {import('./types.d').ApiConfig} ApiConfig */
/** @typedef {import('./types.d').BaseRequestConfig} BaseRequestConfig */
/** @typedef {import('./types.d').LoginParams} LoginParams */
/** @typedef {import('./types.d').LoginData} LoginData */
/** @typedef {import('./types.d').BookingDetailsParams} BookingDetailsParams */
/** @typedef {import('./types.d').EmailPreviewData} EmailPreviewData */
/** @typedef {import('./types.d').BookingListData} BookingListData */
/** @typedef {import('./types.d').RetryStripePaymentsParams} RetryStripePaymentsParams */

/** @typedef {import('axios').AxiosResponse<LoginData>} LoginResponse */
/** @typedef {import('axios').AxiosResponse<EmailPreviewData>} EmailPreviewResponse */
/** @typedef {import('axios').AxiosResponse<BookingListData>} BookingListResponse */

class Api {
  /**
   * @param {ApiConfig} config
   */
  constructor({ baseUrl }) {
    /**
     * @type {import('axios').AxiosInstance}
     */
    this.axiosService = axios.create({ timeout: 20000 });

    this.axiosService.defaults.baseURL = baseUrl;

    this.axiosService.interceptors.request.use(
      (config) => {
        Object.assign(config.headers, getAuthHeaders());
        return config;
      },
    );

    this.axiosService.interceptors.response.use(
      res => res,
      (err) => {
        if (err.name === 'CanceledError') throw err;

        if (err instanceof AxiosError) {
          const requestError = new RequestError(
            err.response?.data?.error
              ? err.response?.data?.error
              : err.message
          );

          requestError.status = err.response?.status ?? null;
          requestError.data = err.response?.data ?? null;
          requestError.response = err.response ?? null;

          throw requestError;
        }

        throw err;
      },
    );
  }

  /**
   * @param {LoginParams} data
   * @returns {Promise<LoginResponse>}
   */
  async login(data) {
    return this.axiosService.post('/public/login', data);
  }

  async verifyMfa(data) { return this.axiosService.post('/public/login/mfa/verify', data); }
  async resendMfa(data) { return this.axiosService.post('/public/login/mfa/resend', data); }

  async installations() {
    return this.axiosService.get("/installations");
  }

  /**
   * @param {string} endpoint
   */
  async proxy(endpoint) {
    return this.axiosService.get(endpoint);
  }

  async createIntent(installationId, guestEmail) {
    /* Pass guestEmail when known so the API reuses an existing Stripe customer
     * for this email instead of creating a duplicate on every call (DRI-1025).
     * Omitted entirely when absent so the body shape is unchanged for callers
     * without an email. */
    const body = { installationId };
    if (guestEmail) body.guestEmail = guestEmail;
    return this.axiosService.post('/public/create-intent', body);
  }

  /**
   * @param {BookingDetailsParams} data
   */
  async bookingDetails(data) {
    return this.axiosService.post('/bookings/details', data)
  }

  /**
   * @param {string} id
   * @returns {Promise<EmailPreviewResponse>}
   */
  async emailPreview(id) {
    return this.axiosService.get(`/emails/${id}/preview`)
  }

  /**
   * @param {string} id
   */
  async emailResend(id) {
    return this.axiosService.post(`/emails/${id}/resend`)
  }

  /**
   * @param {string} installationId
   * @param {URLSearchParams} params
   * @param {BaseRequestConfig} options
   * @returns {Promise<BookingListResponse>}
   */
  async bookings(installationId, params, options) {
    return this.axiosService.get(`/bookings/${installationId}/preview?${params.toString()}`, options);
  }

  /**
   * @param {RetryStripePaymentsParams} data
   */
  async retryStripePayments(data) {
    const url = new URL('/retry-stripe-payments?stream=1', this.axiosService.defaults.baseURL);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Accept: 'application/x-ndjson',
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new RequestError(errorData.error || response.statusText);
    }

    return response;
  }

  async valetPickup(bookingId, { signal } = {}) {
    const { data } = await this.axiosService.get(
      `/public/valet/${encodeURIComponent(bookingId)}`,
      { signal },
    );
    return data;
  }

  /**
   * What the stay costs if the car leaves at `at` (default now).
   */
  async valetCharge(bookingId, { at, signal } = {}) {
    const { data } = await this.axiosService.get(
      `/public/valet/${encodeURIComponent(bookingId)}/charge`,
      { params: at ? { at } : undefined, signal },
    );
    return data;
  }

  async requestValetPickup(bookingId, { mode, expectedDepartureAt, signal } = {}) {
    const { data } = await this.axiosService.post(
      `/public/valet/${encodeURIComponent(bookingId)}/request`,
      mode ? { mode, expectedDepartureAt } : null,
      { signal },
    );
    return data;
  }

  async setValetDeparture(bookingId, { expectedDepartureAt, signal } = {}) {
    const { data } = await this.axiosService.put(
      `/public/valet/${encodeURIComponent(bookingId)}/departure`,
      { expectedDepartureAt },
      { signal },
    );
    return data;
  }

  async createValetSetupIntent(bookingId, { signal } = {}) {
    const { data } = await this.axiosService.post(
      `/public/valet/${encodeURIComponent(bookingId)}/setup-intent`,
      null,
      { signal },
    );
    return data;
  }

  /**
   * Settle a balance. Sending no scope takes the lot, stay and pickup fees
   * together; 'STAY' leaves the fees for the pickup that incurs them.
   */
  async payValetBalance(bookingId, { scope, signal } = {}) {
    const { data } = await this.axiosService.post(
      `/public/valet/${encodeURIComponent(bookingId)}/pay`,
      scope ? { scope } : null,
      { signal },
    );
    return data;
  }

  async queueBookingPayment(bookingId, { signal } = {}) {
    const { data } = await this.axiosService.post(
      `/booking/${encodeURIComponent(bookingId)}/pay`,
      null,
      { signal },
    );
    return data;
  }

  /**
   * Finish a card capture
   */
  async confirmValetCard(bookingId, { signal } = {}) {
    const { data } = await this.axiosService.post(
      `/public/valet/${encodeURIComponent(bookingId)}/confirm-card`,
      null,
      { signal },
    );
    return data;
  }
}

const api = new Api({
  baseUrl: DRIVO_URL ?? 'http://localhost:4000',
});

export default api;
